import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import axios from "axios";
import decodeSlipQr from "../../utils/decodeSlipQr";
import scbIcon from "../../assets/bankicon/014.webp";
import truemoneyIcon from "../../assets/bankicon/true.webp";
import ktbIcon from "../../assets/bankicon/006.webp";
import kbankIcon from "../../assets/bankicon/004.webp";
import bblIcon from "../../assets/bankicon/002.webp";
import ttbIcon from "../../assets/bankicon/011.webp";
import gsbIcon from "../../assets/bankicon/030.webp";
import krungsriIcon from "../../assets/bankicon/025.webp";
import baacIcon from "../../assets/bankicon/034.webp";
import uobIcon from "../../assets/bankicon/024.webp";
import ghbIcon from "../../assets/bankicon/033.webp";
import cimbIcon from "../../assets/bankicon/022.webp";
import islamIcon from "../../assets/bankicon/066.webp";
import kkpIcon from "../../assets/bankicon/069.webp";
import promptpayIcon from "../../assets/bankicon/promptpay.webp";

const BANK_LOGO_MAP = {
  true: truemoneyIcon,
  "014": scbIcon,
  "006": ktbIcon,
  "004": kbankIcon,
  "002": bblIcon,
  "011": ttbIcon,
  "030": gsbIcon,
  "025": krungsriIcon,
  "034": baacIcon,
  "024": uobIcon,
  "033": ghbIcon,
  "022": cimbIcon,
  "066": islamIcon,
  "069": kkpIcon,
  promptpay: promptpayIcon,
};

function getBankLogo(bankCode) {
  return BANK_LOGO_MAP[bankCode] || scbIcon;
}

const STATUS_META = {
  SUCCESS: {
    label: "ฝากสำเร็จ",
    className: "statusSuccess",
    icon: "solar:check-circle-bold",
  },
  PENDING: {
    label: "รอตรวจสอบ",
    className: "statusPending",
    icon: "solar:clock-circle-bold",
  },
  FAILED: {
    label: "ฝากไม่สำเร็จ",
    className: "statusFailed",
    icon: "solar:close-circle-bold",
  },
};

function formatAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return "-";
  }
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getRemark(row) {
  return row?.error_message || row?.trans_ref || "-";
}

export default function SlipUploadPanel({ apiBaseUrl, userId, styles }) {
  const [amount, setAmount] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [payload, setPayload] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [recipientAccounts, setRecipientAccounts] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const activeRecipient = recipientAccounts[0] || null;

  const handleCopyAccount = () => {
    if (!activeRecipient?.accountNumber) return;
    navigator.clipboard.writeText(activeRecipient.accountNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const canSubmit = useMemo(() => {
    const numericAmount = Number(amount);
    return Boolean(
      payload &&
      userId &&
      activeRecipient &&
      Number.isFinite(numericAmount) &&
      numericAmount > 0,
    );
  }, [activeRecipient, amount, payload, userId]);

  const loadHistory = useCallback(async () => {
    if (!userId) {
      setHistoryRows([]);
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/slip/history`, {
        params: {
          userId,
          page: 1,
          limit: 10,
        },
      });
      setHistoryRows(
        Array.isArray(response?.data?.data) ? response.data.data : [],
      );
    } catch (requestError) {
      console.error("Slip history load failed", requestError);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiBaseUrl, userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadRecipientAccounts = useCallback(async () => {
    setRecipientLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/slip/accounts`);
      setRecipientAccounts(
        Array.isArray(response?.data?.data) ? response.data.data : [],
      );
    } catch (requestError) {
      console.error("Slip recipient accounts load failed", requestError);
      setRecipientAccounts([]);
    } finally {
      setRecipientLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadRecipientAccounts();
  }, [loadRecipientAccounts]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError("");
    setSuccessMessage("");

    if (!file) {
      setSelectedFileName("");
      setPreviewUrl("");
      setPayload("");
      return;
    }

    setDecoding(true);
    setSelectedFileName(file.name);

    try {
      const decoded = await decodeSlipQr(file);
      setPreviewUrl(decoded.previewUrl);
      setPayload(decoded.payload);
    } catch (decodeError) {
      setPreviewUrl("");
      setPayload("");
      setError(decodeError.message || "ไม่สามารถอ่าน QR Code จากภาพได้");
    } finally {
      setDecoding(false);
      event.target.value = "";
    }
  };

  const handleSubmit = async () => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError("กรุณาระบุจำนวนเงินให้ถูกต้อง");
      return;
    }
    if (!payload) {
      setError("กรุณาเลือกภาพสลิปที่อ่าน QR Code ได้");
      return;
    }
    if (!activeRecipient) {
      setError("ยังไม่ได้ตั้งค่าบัญชีรับสลิปที่เปิดใช้งาน");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await axios.post(`${apiBaseUrl}/slip/verify`, {
        userId,
        payload,
        amount: numericAmount,
      });
      const message = response?.data?.message || "ส่งตรวจสอบสลิปเรียบร้อยแล้ว";
      setSuccessMessage(message);
      await loadHistory();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.response?.data?.error ||
          requestError.message ||
          "ตรวจสอบสลิปไม่สำเร็จ",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.slipPanel}>
      <div className={styles.slipIntroCard}>
        <div className={styles.slipIntroIcon}>
          <Icon icon="solar:bill-list-bold-duotone" width={22} />
        </div>
        <div className={styles.slipIntroBody}>
          <div className={styles.slipIntroTitle}>อัพโหลดสลิป</div>
          <div className={styles.slipIntroDesc}>
            ระบบจะอ่าน QR Code จากภาพสลิปในอุปกรณ์ของคุณ แล้วส่งเฉพาะ payload
            ไปตรวจสอบกับ EasySlip
          </div>
        </div>
      </div>

      <div className={styles.slipNoticeBox}>
        {recipientLoading ? (
          <div className={styles.slipNoticeLoading}>
            <Icon icon="solar:refresh-circle-bold-duotone" width={18} />
            กำลังโหลดบัญชีรับสลิป...
          </div>
        ) : activeRecipient ? (
          <>
            <div className={styles.slipNoticeHeader}>
              <Icon icon="solar:shield-warning-bold-duotone" width={18} />
              <span>โปรดโอนเงินเข้าบัญชีด้านล่างเท่านั้น</span>
            </div>
            <div className={styles.slipNoticeBody}>
              <div className={styles.slipNoticeBankRow}>
                <img
                  className={styles.slipNoticeBankLogo}
                  src={getBankLogo(activeRecipient.bankCode)}
                  alt={activeRecipient.bankName}
                  loading="lazy"
                />
                <span className={styles.slipNoticeBankName}>
                  {activeRecipient.bankName}
                </span>
              </div>
              <div className={styles.slipNoticeRow}>
                <span className={styles.slipNoticeLabel}>เลขที่บัญชี</span>
                <div className={styles.slipNoticeCopyGroup}>
                  <span className={styles.slipNoticeValue}>
                    {activeRecipient.accountNumber}
                  </span>
                  <button
                    type="button"
                    className={`${styles.slipCopyBtn} ${copied ? styles.slipCopyBtnCopied : ""}`}
                    onClick={handleCopyAccount}
                  >
                    <Icon
                      icon={
                        copied ? "solar:check-circle-bold" : "solar:copy-bold"
                      }
                      width={14}
                    />
                    {copied ? "คัดลอกแล้ว" : "คัดลอก"}
                  </button>
                </div>
              </div>
              <div className={styles.slipNoticeRow}>
                <span className={styles.slipNoticeLabel}>ชื่อบัญชี</span>
                <span className={styles.slipNoticeValue}>
                  {activeRecipient.accountName}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.slipNoticeEmpty}>
            <Icon icon="solar:danger-triangle-bold-duotone" width={18} />
            ขณะนี้ยังไม่มีบัญชีรับสลิปที่เปิดใช้งาน กรุณาติดต่อเจ้าหน้าที่
          </div>
        )}
      </div>

      <div className={styles.slipFormGrid}>
        <div className={styles.slipInputGroup}>
          <label className={styles.slipLabel}>**กรอกจำนวนเงิน (บาท)</label>
          <input
            className={styles.input}
            type="number"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="ระบุยอดที่ต้องการตรวจสอบ"
            disabled={submitting}
          />
        </div>

        <label className={styles.slipUploadField}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className={styles.slipFileInput}
            disabled={decoding || submitting}
          />
          <div className={styles.slipUploadInner}>
            <Icon icon="solar:upload-square-bold" width={22} />
            <span className={styles.slipUploadTitle}>เลือกภาพสลิป</span>
            <span className={styles.slipUploadHint}>
              รองรับไฟล์ภาพ JPG, PNG, WEBP โดยไม่อัพโหลดภาพจริงขึ้นเซิร์ฟเวอร์
            </span>
            {selectedFileName ? (
              <span className={styles.slipUploadFile}>{selectedFileName}</span>
            ) : null}
          </div>
        </label>
      </div>

      {previewUrl ? (
        <div className={styles.slipPreviewCard}>
          <div className={styles.slipPreviewHeader}>
            <span className={styles.slipPreviewTitle}>
              พรีวิวสลิปก่อนยืนยัน
            </span>
            <span className={styles.slipPreviewPayload}>
              Payload พร้อมตรวจสอบ
            </span>
          </div>
          <div className={styles.slipPreviewBody}>
            <img
              src={previewUrl}
              alt="Slip preview"
              className={styles.slipPreviewImage}
            />
            <div className={styles.slipPayloadBox}>{payload}</div>
          </div>
        </div>
      ) : null}

      {decoding ? (
        <div className={styles.slipNoticeBox}>
          กำลังอ่าน QR Code จากภาพสลิป...
        </div>
      ) : null}
      {error ? <div className={styles.errorBox}>{error}</div> : null}
      {successMessage ? (
        <div className={styles.slipSuccessBox}>{successMessage}</div>
      ) : null}

      <button
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={!canSubmit || submitting || decoding}
      >
        {submitting ? "กำลังตรวจสอบสลิป..." : "ยืนยันอัพโหลดสลิป"}
      </button>

      <div className={styles.slipHistoryCard}>
        <div className={styles.slipHistoryHeader}>
          <div className={styles.slipHistoryHeaderText}>
            <div className={styles.slipHistoryTitle}>ประวัติการอัพโหลดสลิป</div>
            <div className={styles.slipHistoryHint}>
              แสดง 10 รายการล่าสุดของผู้ใช้งานนี้
            </div>
          </div>
          {historyLoading ? (
            <span className={styles.slipHistoryLoading}>กำลังโหลด...</span>
          ) : null}
        </div>

        <div className={styles.slipTableWrap}>
          <table className={styles.slipTable}>
            <thead>
              <tr>
                <th>ยอดเงิน</th>
                <th>ธนาคารที่ฝาก</th>
                <th>สถานะ</th>
                <th>วัน-เวลา</th>
                <th>รายละเอียดอื่นๆ</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length ? (
                historyRows.map((row) => {
                  const statusMeta =
                    STATUS_META[row.status] || STATUS_META.FAILED;
                  return (
                    <tr key={row.id}>
                      <td>{formatAmount(row.amount)}</td>
                      <td>{row.sender_bank || row.receiver_bank || "-"}</td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${styles[statusMeta.className]}`}
                        >
                          <Icon icon={statusMeta.icon} width={16} />
                          {statusMeta.label}
                        </span>
                      </td>
                      <td>{formatDateTime(row.created_at)}</td>
                      <td>{getRemark(row)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className={styles.slipEmptyCell}>
                    ยังไม่มีประวัติการอัพโหลดสลิป
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
